<template>
  <v-dialog
    v-model="dialog"
    light
    width="800"
    @input="closeDialog"
  >
    <v-card
      light
      class="pa-6"
      cols="12"
    >
      <v-form
        ref="form"
        v-model="isFormValid"
      >
        <v-container>
          <v-row>
            <v-col
              cols="12"
              md="12"
            >
              <div class="mb-6 text-h5">
                {{ modalTitle }}
              </div>
            </v-col>
          </v-row>

          <v-row class="mt-0 mb-2">
            <v-col
              cols="12"
              md="12"
            >
              <div class="form-dash-modal__rules">
                <span>
                  {{ $t('UniversesModal.label.important') }}
                </span>
                <ul>
                  <li>{{ $t('UniversesModal.label.firstNote') }}</li>
                  <li>{{ $t('UniversesModal.label.secondNote') }}</li>
                  <li>{{ $t('UniversesModal.label.thirdNote') }}</li>
                </ul>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              md="6"
              class="py-0"
            >
              <v-text-field
                v-model="accountId"
                outlined
                dense
                :rules="existsAndIsNumber"
                :persistent-placeholder="isFormToEdit"
                :hint="displayAccHint ? this.$t('UniversesModal.label.message.error.invalidInfo') : ''"
                :persistent-hint="false"
                :disabled="isFormToEdit"
                :label="$t('UniversesModal.label.input.accountId')"
                @keyup="validateAccMaxLength()"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              class="py-0"
            >
              <v-text-field
                v-model="universeId"
                outlined
                dense
                :rules="existsAndIsNumber"
                :hint="displayUniHint ? this.$t('UniversesModal.label.message.error.invalidInfo') : ''"
                :persistent-placeholder="isFormToEdit"
                :disabled="isFormToEdit"
                :label="$t('UniversesModal.label.input.universeId')"
                @keyup="validateUniMaxLength(universeId)"
              />
            </v-col>

            <v-col
              cols="12"
              md="12"
              class="pb-0"
            >
              <v-combobox
                v-model="currentUsersList"
                outlined
                :dense="!isThereUsers"
                :rules="emailRules"
                :items="[]"
                :label="$t('UniversesModal.label.input.usersAccess')"
                :hint="$t('UniversesModal.label.message.hint.email')"
                multiple
                small-chips
                deletable-chips
                append-icon=""
                :search-input.sync="newUsers"
                @update:search-input="splitUsers"
              />
            </v-col>

            <v-col
              v-if="showCopyDashboard"
              cols="12"
              md="12"
              class="py-0"
            >
              <div>
                <div class="text-h6">
                  {{ $t('UniversesModal.label.title.copyDashboard') }}
                </div>
                <div class="mb-6 text-subtitle-2 grey--text text--darken-2">
                  {{ $t('UniversesModal.label.description') }}
                </div>
              </div>
            </v-col>

            <v-col
              v-if="showCopyDashboard"
              cols="12"
              md="6"
              class="py-0"
            >
              <v-text-field
                v-model="originId"
                outlined
                dense
                :error-messages="originIdErrors"
                :disabled="isFormToEdit"
                :label="$t('UniversesModal.label.input.origin')"
              />
            </v-col>

            <v-col
              cols="12"
              md="12"
              class="py-0"
            >
              <div>
                <div class="text-h6">
                  {{ $t('UniversesModal.label.title.environment') }}
                </div>
                <v-radio-group
                  v-model="environment"
                  mandatory
                  :disabled="isFormToEdit"
                >
                  <v-radio
                    key="production"
                    :label="$t('UniversesModal.label.input.production')"
                    :value="false"
                  />
                  <v-radio
                    key="staging"
                    :label="$t('UniversesModal.label.input.staging')"
                    :value="true"
                  />
                </v-radio-group>
              </div>
            </v-col>

            <v-col
              cols="12"
              md="12"
              class="py-0"
            >
              <div>
                <div class="text-h6">
                  {{ $t('UniversesModal.label.title.contractModel') }}
                </div>
                <v-radio-group
                  v-model="contractModel"
                  mandatory
                  class="py-0"
                >
                  <v-radio
                    key="contract"
                    :label="$t('UniversesModal.label.input.contract')"
                    :value="false"
                  />
                  <v-radio
                    key="trial"
                    :label="$t('UniversesModal.label.input.trial')"
                    :value="true"
                  />
                </v-radio-group>
              </div>
            </v-col>

            <v-col
              cols="12"
              md="6"
              class="py-0"
            >
              <v-menu
                v-if="contractModel"
                :close-on-content-click="true"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="computedDateFormatted"
                    :label="$t('UniversesModal.label.input.expirationDate')"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    :rules="contractModelRules"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="expiresTrialDate"
                />
              </v-menu>
            </v-col>

            <v-col
              v-if="isFormToEdit"
              cols="12"
              md="12"
              class="py-0"
            >
              <div>
                <div class="text-h6 mb-1">
                  {{ $t('UniversesModal.label.title.createdDate') }}
                </div>

                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">
                          {{ $t('UniversesModal.label.table.user') }}
                        </th>
                        <th class="text-left">
                          {{ $t('UniversesModal.label.table.mail') }}
                        </th>
                        <th class="text-left">
                          {{ $t('UniversesModal.label.table.createdDate') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{{ dashboard.audit.createdBy }}</td>
                        <td>{{ dashboard.audit.createdByEmail }}</td>
                        <td>{{ toDate(dashboard.audit.createdDate) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-col>

            <v-col
              v-if="isFormToEdit"
              cols="12"
              md="12"
              class="pt-4 py-0"
            >
              <div>
                <div class="text-h6 mb-1">
                  {{ $t('UniversesModal.label.title.lastUpdate') }}
                </div>

                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">
                          {{ $t('UniversesModal.label.table.user') }}
                        </th>
                        <th class="text-left">
                          {{ $t('UniversesModal.label.table.mail') }}
                        </th>
                        <th class="text-left">
                          {{ $t('UniversesModal.label.table.updatedDate') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{{ dashboard.audit.updatedBy }}</td>
                        <td>{{ dashboard.audit.updatedByEmail }}</td>
                        <td>{{ toDate(dashboard.audit.updatedDate) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-row
        justify="end"
        no-gutters
      >
        <v-btn
          rounded
          outlined
          color="secondary"
          class="mr-2"
          :loading="loadingBtn"
          :disabled="loadingBtn"
          @click="closeDialog"
        >
          {{ $t('UniversesModal.label.button.cancel') }}
        </v-btn>
        <v-btn
          rounded
          color="primary"
          depressed
          :loading="loadingBtn"
          :disabled="loadingBtn"
          @click="validateForm"
        >
          {{ primaryButtonMessage }}
        </v-btn>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import firebase from '@/plugins/firebase';
import formModal from '@/mixins/form-modal';

export default {
  name: 'UniversesModal',
  mixins: [
    formModal,
  ],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    dashboard: {
      type: Object,
      default: () => ({}),
    },
    modalTitle: {
      type: String,
      default: '',
    }
  },
  computed: {
    ...mapState('misc', [
      'formModal',
    ]),
    dialog: {
      get() {
        return this.formModal;
      },
      set(newValue) {
        this.mutateMisc({
          property: 'formModal',
          with: newValue,
        });
      },
    },
    currentUsers: {
      get() {
        return this.currentUsersList;
      },
      set(newValue) {
        this.currentUsersList = newValue;

        this.currentUsersList = this.stringArrayToObjectArray(this.firstUserData);
      },
    },
    showCopyDashboard() {
      return !this.isFormToEdit;
    },
  },
  watch: {
    users() {
      this.updateUsers();
    },
  },
  mounted() {
    this.updateUsers();
  },
  methods: {
    ...mapActions('dashboard', [
      'getUniverses',
    ]),
    closeDialog() {
      this.$nextTick(() => {
        this.loadingBtn = false;

        this.mutateMisc({
          property: 'formModal',
          with: false,
        });
      });
    },
    async validateFormAndCreateDashboard() {
      if (!this.$refs.form.validate()) {
        return false;
      }

      this.loadingBtn = true;

      const dashboard = {
        users: this.currentUsersList.map((email) => this.emailToUserObject(email)),
        userEmails: this.currentUsersList,
        onboardingStatus: {
          key: 'SKIP',
          value: 'Pular',
        },
        expiresTrialDate: this.contractModel ? firebase.firestore.Timestamp.fromDate(new Date(this.expiresTrialDate)) : null,
        audit: {
          createdBy: this.user.name,
          createdByEmail: this.user.email,
          createdDate: firebase.firestore.Timestamp.fromDate(new Date()),
          updatedBy: '',
          updatedByEmail: '',
          updatedDate: '',
        }
      };

      const isTemplateBased = Boolean(this.originId);
      if (isTemplateBased) {
        const dashboardData = await this.getDashboardData(this.originId);

        if (!Boolean(Object.keys(dashboardData).length)) {
          this.loadingBtn = false;
          console.error(this.$t('FormModal.label.message.error.invalidId'));
          return false;
        }

        const { title, description, cards } = dashboardData;

        const copiedCards = this.removeUncopyableFiltersFromCards(cards);

        dashboard.title = title;
        dashboard.cards = copiedCards;
        dashboard.description = description || '';
      }

      await this.createDashboard({
        dashboard,
        accountId: this.accountId,
        universeId: this.universeId,
        isStaging: this.environment,
      });

      this.closeDialog();

      this.$nextTick(() => {
        this.$emit('load-dashboards');
      });
    },
    async validateFormAndSaveDashboard() {
      if (!this.$refs.form.validate()) {
        return false;
      }

      // If there is no email in the list of current users, you must remove it from the list of users
      // This operation must be done first, as there may be users who have already started Onboarding
      const addedUsers = [];
      const currentNewUsers = [...this.users].filter((user) => {
        const isUserAlreadyInUsers = this.currentUsers?.includes(user.email);

        if (isUserAlreadyInUsers) addedUsers.push(user.email);

        return isUserAlreadyInUsers;
      });

      // If there is email not yet added to the users list, it must be add
      this.currentUsers.forEach((email) => {
        if (!addedUsers.includes(email)) {
          currentNewUsers.push(this.emailToUserObject(email));
        }
      });

      this.loadingBtn = true;
      await this.editPermission({
        universeId: this.dashboard.universeId,
        dashboardId: this.dashboard.dashboardId,
        users: currentNewUsers,
        userEmails: currentNewUsers.map(({ email }) => email),
        expiresTrialDate: this.contractModel ? firebase.firestore.Timestamp.fromDate(new Date(this.expiresTrialDate)) : null,
        audit: {
          updatedBy: this.user.name,
          updatedByEmail: this.user.email,
          updatedDate: firebase.firestore.Timestamp.fromDate(new Date()),
        }
      });

      this.closeDialog();

      this.$nextTick(() => {
        this.$emit('load-dashboards');
      });
    },
    validateForm() {
      return this.isFormToEdit ? this.validateFormAndSaveDashboard() : this.validateFormAndCreateDashboard();
    },
    updateUsers() {
      this.currentUsersList = this.users.filter(({ email }) => email).map(({ email }) => email);
    }
  },
};
</script>

<style scoped lang="scss">
  .form-dash-modal {
    &__rules {
      font-weight: var(--ds-font-weight-regular);
      font-size: var(--ds-font-size-sm);
      color: var(--ds-color-text-2);
    }
  }

  .v-input {
    padding: 0;
    margin-top: var(--ds-spacing-stack-nano);
  }
</style>
